import { Logotype } from "../components/PixelArt";
import { useEffect, useRef, useState } from "preact/hooks";
import { api } from "../api";
import { playAcknowledgement } from "../audio";
import { ErrorBox } from "../components/Shell";
import { isStandalone } from "../components/InstallHint";
import { StoreBadges } from "../components/StoreBadges";
import type { AuthState } from "../../shared/types";
import { PASSWORD_COPY } from "../../shared/passwords.ts";
import { EMPTY_GATE_FIELDS, NAME_MAX, gateSubmission, inviteFieldValue, type GateFields, type GateMode, type GateSubmission } from "../members-ui.ts";
import { gateOffersPasskey, isQuietAbort, passkeyErrorMessage, serverAnswered } from "../passkeys-ui.ts";
import { autofillAvailable, browserHasWebAuthn, cancelPasskeyCeremony, signInWithPasskey } from "../passkeys.ts";
import { gateLeadsWithPerson } from "../gate-memory.ts";
import { BusyLabel } from "../components/BusyLabel";
import { gateMissing, type Missing } from "../../shared/form-missing.ts";

/**
 * Shown when the app could not ask the server whether this device is signed
 * in: no connection, or the server is down. Distinct from the login gate, so
 * an offline launch never reads as a server misconfiguration.
 */
export function Unreachable({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <main class="gate">
      <img class="gate-logo" src="/ui/peon-original@3x.png" alt="" />
      <Logotype />
      <p class="muted">Omie's Peons needs a connection to fetch the household's tasks.</p>
      <ErrorBox message={message} onRetry={onRetry} />
    </main>
  );
}

/** One line of copy per form, under the logotype. */
function gateLine(mode: GateMode, adding: boolean): string {
  if (mode === "person") return PASSWORD_COPY.signInLine;
  if (mode === "invite") return "Enter your invite code.";
  if (mode === "create") return "Start a new household.";
  return adding ? "Enter that household's password." : "Enter the household password.";
}

export function Login({
  configured,
  onSuccess,
  adding,
  onCancel,
  createNeedsCode = false,
  joinCode,
  onJoinCodeUsed,
  passkeysAvailable,
  name,
  notice,
}: {
  configured: boolean;
  onSuccess: (state: AuthState) => void;
  /** True when this device is already signed into another household and is adding a second one, rather than logging in fresh. */
  adding?: boolean;
  onCancel?: () => void;
  /** The server wants the creation code on the create form (AuthState.create_needs_code). */
  createNeedsCode?: boolean;
  /** A code from a `/join/<code>` link: the invite form opens with it and submits at once. */
  joinCode?: string | null;
  /** Called once the link's code has been tried, so the app can drop it from the address bar. */
  onJoinCodeUsed?: () => void;
  /** The server says passkeys work on this host (AuthState.passkeys_available). */
  passkeysAvailable?: boolean;
  /** The name this device goes by (the name gate's), filled into the Sign in form. */
  name?: string | null;
  /** Why the gate is showing, once (src/app.tsx): "You were signed out of Omie's." */
  notice?: string | null;
}) {
  // The household password form comes first: it is the door every newcomer has, and a peon with no
  // password of their own typed it into "Sign in" and was told "Wrong name or password". A device
  // that has signed in as a person before, or whose name is one it knows is held, leads with Sign in
  // instead (src/gate-memory.ts). The other is one quiet link away (docs/members.md "Personal passwords").
  const [home] = useState<"person" | "password">(() => (gateLeadsWithPerson(name) ? "person" : "password"));
  const [mode, setMode] = useState<GateMode>(joinCode ? "invite" : home);
  /** The sign-in form the invite or create form was opened from: their Back returns there. */
  const [backTo, setBackTo] = useState<"person" | "password">(home);
  const [fields, setFields] = useState<GateFields>(() => ({ ...EMPTY_GATE_FIELDS, code: joinCode ?? "", personName: name ?? "" }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (key: keyof GateFields, value: string) => setFields((f) => ({ ...f, [key]: key === "code" ? inviteFieldValue(value) : value }));

  const submission = gateSubmission(mode, fields, createNeedsCode);
  // Each form's primary stays live while it is incomplete (docs/ui-audit.md triage item 1): a press
  // says the first missing thing and puts the focus on that field. The line goes once it is filled.
  const unready = gateMissing(mode, fields, createNeedsCode);
  const [missing, setMissing] = useState<Missing | null>(null);
  useEffect(() => {
    // As an update: a press that set a new line before this effect ran keeps it.
    if (missing && unready?.field !== missing.field) setMissing((m) => (m && unready?.field !== m.field ? null : m));
  }, [missing, unready?.field]);
  const missingLine = missing && (unready?.field === missing.field ? unready : missing).line;

  // "Sign in with a passkey" (docs/members.md "People and passkeys"): on the home, where the
  // browser has WebAuthn. Not behind the workshop's `passkeys` switch: the gate is signed out and
  // a new browser has never seen the household's flags, so a passkey must open the door by itself
  // (the native gate has the same rule); the switch gates the sheets.
  const [offerPasskey] = useState(() => configured && gateOffersPasskey(passkeysAvailable, browserHasWebAuthn()));
  const [autofill, setAutofill] = useState(false);

  // A browser honours autofocus once per page, and the name gate has had it by now: each form moves
  // the focus itself to the field it marks (the Sign in form's password when the name came along).
  useEffect(() => {
    const field = document.querySelector<HTMLInputElement>(".gate-form [autofocus]");
    if (field && document.activeElement !== field) field.focus();
  }, [mode]);

  // Where the browser can, a passkey also waits in the field's suggestions from the start (the Sign
  // in form's name, the household password form's password). Called off when the button starts its
  // own request (the library aborts the older one) or the gate goes.
  useEffect(() => {
    if (!offerPasskey || (mode !== "password" && mode !== "person")) return;
    let live = true;
    void (async () => {
      if (!(await autofillAvailable()) || !live) return;
      setAutofill(true);
      try {
        const state = await signInWithPasskey(true);
        if (live) onSuccess(state);
      } catch (e) {
        // Only the server's answer is worth a word here; the browser calling the quiet request off is not.
        if (live && !isQuietAbort(e) && serverAnswered(e)) setError(passkeyErrorMessage(e));
      }
    })();
    return () => {
      live = false;
      cancelPasskeyCeremony();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerPasskey, mode]);

  async function passkey() {
    if (busy) return;
    void playAcknowledgement();
    setBusy(true);
    setError(null);
    try {
      onSuccess(await signInWithPasskey(false));
    } catch (e) {
      setError(passkeyErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function send(sub: GateSubmission) {
    if (!sub.ok) {
      if (sub.error) setError(sub.error);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const state =
        sub.path === "/api/auth/person/login"
          ? await api.auth.person(sub.body.name, sub.body.password)
          : sub.path === "/api/auth/login"
            ? await api.auth.login(sub.body.password)
            : sub.path === "/api/auth/join"
              ? await api.auth.join(sub.body.code)
              : await api.auth.create(sub.body);
      onSuccess(state);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
    } finally {
      setBusy(false);
    }
  }

  async function submit(e: Event) {
    e.preventDefault();
    if (busy) return;
    if (unready) {
      setError(null);
      setMissing(unready);
      (e.currentTarget as HTMLFormElement).querySelector<HTMLInputElement>(`[data-field="${unready.field}"]`)?.focus();
      return;
    }
    // Submits keep their chiptune (docs/product.md), once the form is complete: an empty or
    // mistyped field plays nothing.
    if (submission.ok) void playAcknowledgement();
    await send(submission);
  }

  // A `/join/<code>` link joins straight away (docs/members.md), once.
  const joined = useRef(false);
  useEffect(() => {
    if (!joinCode || joined.current || !configured) return;
    joined.current = true;
    const sub = gateSubmission("invite", { ...EMPTY_GATE_FIELDS, code: joinCode }, createNeedsCode);
    void send(sub).finally(() => onJoinCodeUsed?.());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joinCode, configured]);

  function switchMode(next: GateMode) {
    if ((next === "invite" || next === "create") && (mode === "person" || mode === "password")) setBackTo(mode);
    setMode(next);
    setError(null);
    setMissing(null);
  }
  /** Where Back goes: the invite and create forms return to the form they came from; the second sign-in form to the first. */
  const back: GateMode | null = mode === "invite" || mode === "create" ? backTo : mode !== home ? home : null;

  const cancel = adding && (
    <button type="button" class="btn btn-ghost" onClick={onCancel} disabled={busy}>
      Cancel
    </button>
  );

  // The store badges sit at the foot of the gate in a browser, never once the app is installed.
  const badges = configured && !isStandalone();

  return (
    <main class={badges ? "gate gate-with-footer" : "gate"}>
      <img class="gate-logo" src="/ui/peon-original@3x.png" alt="" />
      <Logotype />
      {configured ? (
        <>
          {adding && <p class="muted">Add a household</p>}
          {notice && <p class="muted small gate-notice" role="status">{notice}</p>}
          <p class="muted">{gateLine(mode, !!adding)}</p>
          {mode === "person" && (
            <form class="gate-form" onSubmit={submit}>
              <input
                class="input"
                type="text"
                autocomplete={autofill ? "username webauthn" : "username"}
                autoCapitalize="words"
                autoCorrect="off"
                spellcheck={false}
                autoFocus={!fields.personName}
                maxLength={60}
                placeholder={PASSWORD_COPY.nameField}
                aria-label={PASSWORD_COPY.nameField}
                data-field="personName"
                value={fields.personName}
                onInput={(e) => set("personName", e.currentTarget.value)}
              />
              <input
                class="input"
                type="password"
                autocomplete="current-password"
                autoFocus={!!fields.personName}
                placeholder={PASSWORD_COPY.passwordField}
                aria-label={PASSWORD_COPY.passwordField}
                data-field="personPassword"
                value={fields.personPassword}
                onInput={(e) => set("personPassword", e.currentTarget.value)}
              />
              <button class={`btn btn-primary${unready ? " btn-unready" : ""}`} type="submit" disabled={busy} aria-describedby={missingLine ? "gate-missing" : undefined}>
                <BusyLabel busy={busy} label="Checking…">{PASSWORD_COPY.enter}</BusyLabel>
              </button>
              {offerPasskey && (
                <button type="button" class="btn btn-ghost" onClick={() => void passkey()} disabled={busy}>
                  Sign in with a passkey
                </button>
              )}
              {cancel}
              {missingLine ? (
                <div class="form-error" role="alert" id="gate-missing">{missingLine}</div>
              ) : (
                error && <div class="form-error" role="alert">{error}</div>
              )}
            </form>
          )}
          {mode === "password" && (
            <form class="gate-form" onSubmit={submit}>
              <input
                class="input"
                type="password"
                autocomplete={autofill ? "current-password webauthn" : "current-password"}
                autoFocus
                placeholder="Password"
                aria-label="Household password"
                data-field="password"
                value={fields.password}
                onInput={(e) => set("password", e.currentTarget.value)}
              />
              <button class={`btn btn-primary${unready ? " btn-unready" : ""}`} type="submit" disabled={busy} aria-describedby={missingLine ? "gate-missing" : undefined}>
                <BusyLabel busy={busy} label="Checking…">Enter</BusyLabel>
              </button>
              {offerPasskey && (
                <button type="button" class="btn btn-ghost" onClick={() => void passkey()} disabled={busy}>
                  Sign in with a passkey
                </button>
              )}
              {cancel}
              {missingLine ? (
                <div class="form-error" role="alert" id="gate-missing">{missingLine}</div>
              ) : (
                error && <div class="form-error" role="alert">{error}</div>
              )}
            </form>
          )}
          {mode === "invite" && (
            <form class="gate-form" onSubmit={submit}>
              <input
                class="input gate-code"
                type="text"
                autocomplete="one-time-code"
                autoCapitalize="characters"
                autoCorrect="off"
                spellcheck={false}
                autoFocus
                placeholder="Invite code"
                aria-label="Invite code"
                data-field="code"
                value={fields.code}
                onInput={(e) => {
                  // No maxLength: a pasted "ABCD-EFGH" is cut to length only after the dash is gone.
                  const v = inviteFieldValue(e.currentTarget.value);
                  e.currentTarget.value = v;
                  set("code", v);
                }}
              />
              <button class={`btn btn-primary${unready ? " btn-unready" : ""}`} type="submit" disabled={busy} aria-describedby={missingLine ? "gate-missing" : undefined}>
                <BusyLabel busy={busy} label="Checking…">Join</BusyLabel>
              </button>
              {cancel}
              {missingLine ? (
                <div class="form-error" role="alert" id="gate-missing">{missingLine}</div>
              ) : (
                error && <div class="form-error" role="alert">{error}</div>
              )}
            </form>
          )}
          {mode === "create" && (
            <form class="gate-form" onSubmit={submit}>
              <input
                class="input"
                type="text"
                autoFocus
                maxLength={NAME_MAX}
                placeholder="Household name"
                aria-label="Household name"
                data-field="name"
                value={fields.name}
                onInput={(e) => set("name", e.currentTarget.value)}
              />
              <input
                class="input"
                type="password"
                autocomplete="new-password"
                placeholder="Password"
                aria-label="Password"
                data-field="newPassword"
                value={fields.newPassword}
                onInput={(e) => set("newPassword", e.currentTarget.value)}
              />
              <input
                class="input"
                type="password"
                autocomplete="new-password"
                placeholder="Password again"
                aria-label="Password again"
                data-field="again"
                value={fields.again}
                onInput={(e) => set("again", e.currentTarget.value)}
              />
              {createNeedsCode && (
                <input
                  class="input"
                  type="text"
                  autoCorrect="off"
                  spellcheck={false}
                  placeholder="Creation code"
                  aria-label="Creation code"
                  data-field="creationCode"
                  value={fields.creationCode}
                  onInput={(e) => set("creationCode", e.currentTarget.value)}
                />
              )}
              <button class={`btn btn-primary${unready ? " btn-unready" : ""}`} type="submit" disabled={busy} aria-describedby={missingLine ? "gate-missing" : undefined}>
                <BusyLabel busy={busy}>Create household</BusyLabel>
              </button>
              {cancel}
              {missingLine ? (
                <div class="form-error" role="alert" id="gate-missing">{missingLine}</div>
              ) : (
                error && <div class="form-error" role="alert">{error}</div>
              )}
            </form>
          )}
          {/* The other ways in, quiet under the form (docs/members.md "The clients"). */}
          <div class="gate-links">
            {back && (
              <button type="button" class="btn-quiet" onClick={() => switchMode(back)} disabled={busy}>
                Back
              </button>
            )}
            {mode === home && (
              <button type="button" class="btn-quiet" onClick={() => switchMode(home === "person" ? "password" : "person")} disabled={busy}>
                {home === "person" ? PASSWORD_COPY.useHousehold : "Sign in with your own password"}
              </button>
            )}
            {(mode === "person" || mode === "password") && (
              <>
                <button type="button" class="btn-quiet" onClick={() => switchMode("invite")} disabled={busy}>
                  Join with an invite code
                </button>
                <button type="button" class="btn-quiet" onClick={() => switchMode("create")} disabled={busy}>
                  Start a new household
                </button>
              </>
            )}
          </div>
          {badges && (
            <footer class="gate-footer">
              <StoreBadges />
            </footer>
          )}
        </>
      ) : (
        <p class="form-error">
          The server has no APP_PASSWORD configured. Set one with <code>wrangler pages secret put APP_PASSWORD</code> (or in
          .dev.vars locally).
        </p>
      )}
    </main>
  );
}
